import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import {
    PENDING, IDLE,
    CONFIDENCE_LOW, CONFIDENCE_MEDIUM, CONFIDENCE_HIGH,
    INCREASE_TO, DECREASE_TO, STAY_ABOVE, STAY_BELOW, ACHIEVED,
    DATE_FORMAT,
    WARNING,
} from "const"
import { OkrKeyResult, OkrKeyResultUpdate, OkrKeyResultUpdateCreate } from "server/models"
import { StateType, useInput, deepCopy } from "client/utils"
import {
    StoreType,
    OkrKeyResultList,
    OkrKeyResultUpdateValidation,

    SaveOkrKeyResultUpdate,
    LoadOkrKeyResultUpdates,
    UpdateOkrKeyResultActualNumber,
    LoadOkrKeyResultById,

    setOkrKeyResultUpdateList,
    setOkrKeyResultUpdateEdit,
    setOkrKeyResultUpdateReloadEdit,
    setOkrKeyResultList,
    closeModal,
} from "client/store"
import {
    PageForm,
    AlertMessage,
    FormGroup,
    Label,
    Input,
    DivInput,
    Textarea,
    RadioButton,
    Feedback,
    DatePicker,
    Button,
    DateFormatterString,
    NotificationIcon,
    SpinnerIcon,
} from "client/components"

import { MetricInput } from "client/pages/okr/keyResult/styled"
import {
    DeadlineNotificationWrapper,
    ConfidenceButton,
} from "../styled"

let isMounted: boolean

interface FormProps {
    update: OkrKeyResultUpdate
    keyResult: OkrKeyResult
}

const Form = ({
    update,
    keyResult,
}: FormProps) => {
    const updateList = useSelector((state: StoreType) => state.okrKeyResultUpdate.updateList)
    const keyResultList = useSelector((state: StoreType) => state.okrKeyResult.keyResultList)
    const session = useSelector((state: StoreType) => state.okrSession.sessionView)
    const metricList = useSelector((state: StoreType) => state.okrMetric.metricList)

    const userData = useSelector((state: StoreType) => state.system.userData)
    const modals = useSelector((state: StoreType) => state.system.modals)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [note, setNote] = useInput(update.note)
    const [value, setValue] = useState(update.value !== null ? update.value.toString() : "")
    const [confidenceLevel, setConfidenceLevel] = useState(update.confidenceLevel)
    const [dateUpdate, setDateUpdate] = useState(update.dateUpdate ? new Date(update.dateUpdate) : null)

    const [validation, setValidation] = useState<OkrKeyResultUpdateValidation>(null)

    const [saveState, setSaveState] = useState<StateType>(IDLE)
    const [applyState, setApplyState] = useState<StateType>(IDLE)

    // key result deadline
    let deadline: Date = null
    if (keyResult.type !== STAY_ABOVE && keyResult.type !== STAY_BELOW && keyResult.deadlineType !== null) {
        deadline = new Date(keyResult.deadlineHard)
    }
    const deadlineString = DateFormatterString(deadline, lang)

    // date update limits
    const today = new Date()
    const minDate = new Date(session.dateStart)
    let maxDate = new Date(session.dateEnd)
    if (deadline && deadline < maxDate) maxDate = deadline
    if (today < maxDate) maxDate = today

    if (update && update.id) {
        const krUpdates = updateList[keyResult.id] || []
        const lastUpdate = krUpdates[krUpdates.length - 1]
        if (lastUpdate) {
            const lastUpdateDate = new Date(lastUpdate.dateUpdate)
            if (lastUpdateDate > maxDate) maxDate = lastUpdateDate
        }
    }

    useEffect(() => {
        isMounted = true
        return () => {
            isMounted = false
        }
    }, [])

    useEffect(() => {
        if (update.id) return // only for new updates

        const krUpdates = updateList[keyResult.id] || []
        const lastUpdate = krUpdates[krUpdates.length - 1]
        if (lastUpdate) {
            setConfidenceLevel(lastUpdate.confidenceLevel)
        }
        setDateUpdate(maxDate < today ? maxDate : today)

        if (keyResult.type === ACHIEVED) {
            setValue(keyResult.actualNumber.toString())
        }
    }, [update, updateList])

    const onClose = () => {
        // this page can be opened only via modal
        if (modals.length && isMounted) dispatch(closeModal())
    }

    const onSave = (mode: number) => {
        if (mode === 1) setSaveState(PENDING)
        if (mode === 2) setApplyState(PENDING)

        const newUpdateData: OkrKeyResultUpdateCreate = {
            keyResultId: keyResult.id,
            author: update.author || userData.userId,
            value: parseFloat(value),
            confidenceLevel,
            note,
            // convert date to GMT 0 for correct saving in DB
            dateUpdate: new Date(Date.UTC(dateUpdate.getFullYear(), dateUpdate.getMonth(), dateUpdate.getDate())),
        }

        SaveOkrKeyResultUpdate(
            update.id,
            newUpdateData,
            lang,
        ).then((res) => {
            if (isMounted) {
                setValidation(res.validation)
                if (res.validation) {
                    setSaveState(IDLE)
                    setApplyState(IDLE)
                }
            }
            if (res.validation) return

            dispatch(setOkrKeyResultUpdateEdit({
                id: res.newUpdateId || update.id,
                ...newUpdateData,
            }))

            // reload update list
            LoadOkrKeyResultUpdates(keyResult).then((updates) => {
                dispatch(setOkrKeyResultUpdateList({ [keyResult.id]: updates }))

                // update key result active value
                const lastUpdate = updates[updates.length - 1]
                UpdateOkrKeyResultActualNumber({
                    id: keyResult.id,
                    actualNumber: lastUpdate ? lastUpdate.value : keyResult.initialNumber,
                }).then(() => {
                    LoadOkrKeyResultById(keyResult.id, keyResult.objectiveId, keyResult.sessionId)
                    .then((updatedKeyResult) => {
                        const keyResultListCopy: OkrKeyResultList = deepCopy(keyResultList)
                        keyResultListCopy[keyResult.id] = updatedKeyResult
                        dispatch(setOkrKeyResultList(keyResultListCopy))

                        if (isMounted) {
                            setSaveState(IDLE)
                            setApplyState(IDLE)
                        }

                        if (mode === 1) onClose()
                        if (mode === 2) {
                            dispatch(setOkrKeyResultUpdateReloadEdit(false))
                            setTimeout(() => {
                                dispatch(setOkrKeyResultUpdateReloadEdit(true))
                            }, 0)
                        }
                    })
                })
            })
        })
    }

    // current KR value description
    const metric = metricList[keyResult.metricId] || null

    let currentValue: any
    if (keyResult.type !== ACHIEVED) {
        const metricBefore = metric && !metric.displayAfter ? `${metric.symbol} ` : ""
        const metricAfter = metric && metric.displayAfter ? ` ${metric.symbol}` : ""

        const actual = `${metricBefore}${keyResult.actualNumber}${metricAfter}`
        const target = `${metricBefore}${keyResult.targetNumber}${metricAfter}`
        const critical = keyResult.useCriticalTarget && keyResult.criticalNumber !== null
            ? `${metricBefore}${keyResult.criticalNumber}${metricAfter}`
            : ""

        let descriptionStart: string
        let descriptionEnd = ""
        if (keyResult.type === INCREASE_TO) descriptionStart = lang.shouldIncreaseTo
        if (keyResult.type === DECREASE_TO) descriptionStart = lang.shouldDecreaseTo
        if (keyResult.type === STAY_ABOVE) {
            descriptionStart = lang.shouldStayAbove
            descriptionEnd = lang.mustBeAbove
        }
        if (keyResult.type === STAY_BELOW) {
            descriptionStart = lang.shouldStayBelow
            descriptionEnd = lang.mustBeBelow
        }
        currentValue = `${actual}, ${descriptionStart} ${target} ${descriptionEnd} ${critical}`
    }

    return (
        <PageForm>
            <Row>
                <Col xs={12}>
                    {
                        deadline && (
                            <AlertMessage variant={WARNING}>
                                <DeadlineNotificationWrapper>
                                    <NotificationIcon />
                                    {lang.hardDeadlineForKeyResult.replace("{0}", deadlineString.date)}
                                </DeadlineNotificationWrapper>
                            </AlertMessage>
                        )
                    }
                    <FormGroup>
                        <Label>{lang.keyResult}</Label>
                        <Input type="text" value={keyResult.title} readOnly />
                    </FormGroup>
                    {
                        keyResult.type !== ACHIEVED && (
                            <FormGroup>
                                <Label>{lang.currentValue}</Label>
                                <DivInput widePadding>{currentValue}</DivInput>
                            </FormGroup>
                        )
                    }
                    {
                        keyResult.type !== ACHIEVED && (
                            <FormGroup>
                                <Label>{lang.newValue}</Label>
                                <MetricInput
                                    metric={metric ? metric.symbol : null}
                                    metricAfter={metric ? metric.displayAfter : null}
                                    value={value}
                                    onChange={(e) => setValue(e.target.value)}
                                />
                                <Feedback>{validation && validation.value}</Feedback>
                            </FormGroup>
                        )
                    }
                    {
                        keyResult.type === ACHIEVED && (
                            <FormGroup>
                                <Label>{lang.status}</Label>
                                <FormGroup inline>
                                    <RadioButton
                                        checked={value === "0"}
                                        onChange={() => setValue("0")}
                                        title={lang.notAchievedYet}
                                    />
                                    <RadioButton
                                        checked={value === "1"}
                                        onChange={() => setValue("1")}
                                        title={lang.achieved}
                                    />
                                </FormGroup>
                                <Feedback>{validation && validation.value}</Feedback>
                            </FormGroup>
                        )
                    }

                    <FormGroup>
                        <Label>{lang.confidenceLevel}</Label>
                        <FormGroup inline>
                            <ConfidenceButton
                                confidenceLevel={CONFIDENCE_LOW}
                                isActive={confidenceLevel === CONFIDENCE_LOW}
                                onClick={() => setConfidenceLevel(CONFIDENCE_LOW)}
                            >
                                {lang.low}
                            </ConfidenceButton>
                            <ConfidenceButton
                                confidenceLevel={CONFIDENCE_MEDIUM}
                                isActive={confidenceLevel === CONFIDENCE_MEDIUM}
                                onClick={() => setConfidenceLevel(CONFIDENCE_MEDIUM)}
                            >
                                {lang.medium}
                            </ConfidenceButton>
                            <ConfidenceButton
                                confidenceLevel={CONFIDENCE_HIGH}
                                isActive={confidenceLevel === CONFIDENCE_HIGH}
                                onClick={() => setConfidenceLevel(CONFIDENCE_HIGH)}
                            >
                                {lang.high}
                            </ConfidenceButton>
                        </FormGroup>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.note}</Label>
                        <Textarea value={note} onChange={setNote} />
                        <Feedback>{validation && validation.note}</Feedback>
                    </FormGroup>

                    <FormGroup>
                        <Label>{lang.dateOfUpdate}</Label>
                        <DatePicker
                            selected={dateUpdate}
                            onChange={(date: Date) => {
                                if (!date) setDateUpdate(dateUpdate)
                                else setDateUpdate(date)
                            }}
                            minDate={minDate}
                            maxDate={maxDate}
                            dateFormat={DATE_FORMAT}
                        />
                        <Feedback>{validation && validation.dateUpdate}</Feedback>
                    </FormGroup>
                </Col>
                <Col xs={12}>
                    <Button variant="success" row onClick={() => onSave(1)}>
                        {saveState === PENDING ? <SpinnerIcon /> : lang.save}
                    </Button>
                    <Button variant="success" row onClick={() => onSave(2)}>
                        {applyState === PENDING ? <SpinnerIcon /> : lang.apply}
                    </Button>
                    <Button row onClick={onClose}>{lang.cancel}</Button>
                </Col>
            </Row>
        </PageForm>
    )
}
export default Form
